import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import DoDisturbOnOutlinedIcon from '@mui/icons-material/DoDisturbOnOutlined';
import { Divider } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useMutation } from '@tanstack/react-query';
import isEqual from 'lodash.isequal';
import { useForm, useWatch } from 'react-hook-form';

import { ProductFeatures } from 'common/src/types/enums/ProductFeatures.enum';
import { IMeter, IUtility } from 'common/src/types/interfaces';
import {
  ICreateUtilityDto,
  IUpdateUtilityDto,
} from 'common/src/types/utility.dtos';

import { addUtility, updateUtility } from 'portal/src/api/UtilitiesService';
import useAuth from 'portal/src/auth/useAuth';
import DataTable from 'portal/src/components/common/DataTable';
import { getAddressFields } from 'portal/src/components/common/forms/commonFormFields';
import { FormContainer } from 'portal/src/components/common/forms/FormContainer';
import { prepareFormPayload } from 'portal/src/components/common/forms/handleFormData';
import useFormControlError from 'portal/src/components/common/forms/useFormControlError';
import addBooleanColumnProps from 'portal/src/components/common/tables/addBooleanColumnProps';
import addDateColumnProps from 'portal/src/components/common/tables/addDateColumnProps';
import { masterSubAccountColumn } from 'portal/src/components/common/tables/masterSubColumns';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import { isActiveAccount } from 'portal/src/utils/checkActive';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

import {
  defaultValues,
  getFields,
  UtilityFormValues,
} from './UtilityForm.fields';

interface FormComponentProps {
  entity?: Partial<IUtility>;
  handleBackAction: () => void;
}

const UtilityForm = ({ entity = {}, handleBackAction }: FormComponentProps) => {
  const { featureChecker, getUser, hasSystemAdminAccess } = useAuth();
  const { organizationId } = getUser();
  const snackbarHandler = useSnackbarHandler();

  const isDeactivated = entity.id && !entity.isActive;

  const formFieldsConfig = getFields(hasSystemAdminAccess());

  const gridColumns: GridColDef[] = [
    {
      field: 'isActive',
      headerName: 'Active',
      valueGetter: (_, row: IMeter) => isActiveAccount(row),
      ...addBooleanColumnProps({
        labels: {
          yesLabel: (
            <CheckCircleOutlineOutlinedIcon
              color="success"
              fontSize="small"
              aria-label="Active"
            />
          ),
          noLabel: (
            <DoDisturbOnOutlinedIcon
              color="action"
              fontSize="small"
              aria-label="Inactive"
            />
          ),
        },
        width: 90,
      }),
    },
    {
      field: 'number',
      headerName: 'Account Number',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'serviceStartDate',
      flex: 1,
      ...addDateColumnProps('Service Start Date'),
    },
    {
      field: 'serviceEndDate',
      flex: 1,
      ...addDateColumnProps('Service End Date'),
    },
    ...(featureChecker.productFeatureEnabled(ProductFeatures.allocations)
      ? [masterSubAccountColumn]
      : []),
  ];

  const {
    control,
    handleSubmit,
    formState: { dirtyFields, isDirty },
    getValues,
    setError,
  } = useForm({ defaultValues: defaultValues(entity) });
  const [selectedCountry] = useWatch({ control, name: ['country'] });
  const mainFieldsConfig = [
    ...formFieldsConfig,
    ...getAddressFields(false, false, selectedCountry).map(
      (fieldConf, index) =>
        index === 0
          ? { ...fieldConf, prependDivider: 'Billing Address' }
          : fieldConf,
    ),
  ];
  const { getSnackbarApiErrorMessage } = useFormControlError({
    getValues,
    setError,
  });

  const { mutate: mutateUtility, isPending } = useMutation({
    mutationFn: async (formValues: UtilityFormValues) => {
      const payload = prepareFormPayload(formValues, {
        ...dirtyFields,
        serviceTypes: !isEqual(entity.serviceTypes, formValues.serviceTypes),
      });

      const {
        address1: billingAddress1,
        address2: billingAddress2,
        city: billingCity,
        country: billingCountry,
        postalCode: billingPostalCode,
        state: billingState,
        ...restValues
      } = payload;

      const finalPayload = {
        ...restValues,
        billingAddress1,
        billingAddress2,
        billingCity,
        billingCountry,
        billingPostalCode,
        billingState,
      };

      if (entity.id) {
        await updateUtility(entity.id, finalPayload as IUpdateUtilityDto);
        snackbarHandler.success(MESSAGES.UTILITY.UPDATED);
      } else {
        await addUtility({
          ...finalPayload,
          organizationId,
        } as ICreateUtilityDto);
        snackbarHandler.success(MESSAGES.UTILITY.CREATED);
      }

      handleBackAction();
    },
    onError: (err: unknown) => {
      snackbarHandler.error(getSnackbarApiErrorMessage(err));
    },
  });

  return (
    <FormContainer
      control={control}
      isDirty={isDirty}
      handleBackAction={handleBackAction}
      handleSubmit={handleSubmit(async (data) => mutateUtility(data))}
      header="Utility Information"
      isDeactivated={!!isDeactivated}
      isNew={!entity.id}
      isPending={isPending}
      mainFieldsConfig={mainFieldsConfig}
      afterMainFields={
        entity.id && (
          <>
            <Divider sx={{ mt: 2, mb: 1 }}>Associated Accounts</Divider>
            <DataTable
              checkboxSelection={false}
              columns={gridColumns}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'isActive', sort: 'desc' }],
                },
              }}
              isRowNavigation={false}
              rows={entity?.accounts ?? []}
            />
          </>
        )
      }
    />
  );
};

export default UtilityForm;
